import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { useOrganization, useUsers } from "../../../../components/Common/HelperComponents/GetDataFromStore";
import {
  getAudit,
  setAuditAuditors,
  unwrapGet,
  unwrapPost,
  updateAudit,
} from "../../../../helpers/auditHub_helper";
import DueDateField from "../DueDateField";
import AuditScopeSection from "./AuditScopeSection";
import LaunchAuditDialog from "./LaunchAuditDialog";
import { AUDIT_STATUSES, AUDIT_STATUS_LABEL } from "../constants";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";
import { auditorCandidates, excludeMspUsers, formatUserLabel } from "../utils";
import useMspUsers from "../useMspUsers";

const LABEL_COLOR = "#334155";
const FIELD_BORDER = "#e2e8f0";
const CLOSE_BG = "#74788d";
const CLOSE_TEXT = "#eff2f7";
const NOTICE_COLOR = { success: "#16a34a", warning: "#b45309", error: "#dc2626" };

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    minHeight: 40,
    fontSize: "0.875rem",
    color: LABEL_COLOR,
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: FIELD_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: `0 0 0 1px ${AH_TEXT}` },
    "&.Mui-disabled": { backgroundColor: "#f8fafc" },
    "&.Mui-disabled fieldset": { borderColor: FIELD_BORDER },
  },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: LABEL_COLOR },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#64748b" },
  "& .MuiInputBase-input::placeholder": { color: "#74788d", opacity: 1 },
};

const auditorChipSx = {
  height: 24,
  borderRadius: "6px",
  backgroundColor: "#fff4eb",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: "0.8125rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 1 },
  "& .MuiChip-deleteIcon": {
    color: "#9a3412",
    fontSize: 16,
    "&:hover": { color: AH_WHITE, backgroundColor: AH_ACCENT },
  },
};

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const optionPaperSx = {
  borderRadius: "8px",
  mt: 0.5,
  fontSize: "0.875rem",
  "& .MuiAutocomplete-option": {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: AH_TEXT,
  },
  "& .MuiAutocomplete-option.Mui-focused": {
    backgroundColor: "#fff4eb",
  },
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: AH_ACCENT,
    color: AH_WHITE,
  },
  "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
    backgroundColor: AH_ACCENT_HOVER,
  },
};

const FieldLabel = ({ children, required, htmlFor }) => (
  <Box
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: "block",
      mb: "6px",
      fontSize: "0.8125rem",
      fontWeight: 600,
      color: LABEL_COLOR,
    }}
  >
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#dc2626", ml: 0.25 }} aria-hidden="true">
        *
      </Box>
    ) : null}
  </Box>
);

const hasEmail = (value) => typeof value === "string" && value.includes("@") && !/\s/.test(value);
const hasDueDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

const utcToday = () => new Date().toISOString().slice(0, 10);

const emptyLoaded = () => ({
  name: "",
  ownerEmail: "",
  defaultAssigneeEmail: "",
  dueDate: "",
  auditorEmails: [],
});

const sameEmails = (left, right) => {
  const a = [...left].map((email) => String(email).toLowerCase()).sort();
  const b = [...right].map((email) => String(email).toLowerCase()).sort();
  return a.length === b.length && a.every((email, index) => email === b[index]);
};

const EditAuditDialog = ({ auditId, open, onClose, onSaved, onLaunched, onRefresh }) => {
  const organization = useOrganization();
  const { users, usersLoading } = useUsers();
  const mspUsers = useMspUsers();
  const [status, setStatus] = useState("idle");
  const [auditStatus, setAuditStatus] = useState("");
  const [auditCode, setAuditCode] = useState("");
  const [loaded, setLoaded] = useState(emptyLoaded);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    name: "",
    ownerEmail: "",
    defaultAssigneeEmail: "",
    dueDate: "",
    auditorEmails: [],
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const [hasPendingAccounts, setHasPendingAccounts] = useState(false);
  const scopeRef = useRef(null);
  const onPendingChange = useCallback((hasPending) => setHasPendingAccounts(hasPending), []);

  useEffect(() => {
    if (!open || !auditId) return undefined;
    let cancelled = false;
    setStatus("loading");
    setError("");
    setNotice(null);
    setConfirmLaunch(false);
    setHasPendingAccounts(false);
    setAuditCode("");
    getAudit(auditId)
      .then((res) => {
        if (cancelled) return;
        const detail = unwrapGet(res);
        const auditorEmails = Array.isArray(detail.auditorEmails) ? detail.auditorEmails.filter(Boolean) : [];
        const dueDate = detail.dueDate ? String(detail.dueDate).slice(0, 10) : "";
        setAuditStatus(detail.status || "");
        setAuditCode(detail.auditCode || "");
        setLoaded({
          name: (detail.name || "").trim(),
          ownerEmail: detail.ownerEmail || "",
          defaultAssigneeEmail: detail.defaultAssigneeEmail || "",
          dueDate,
          auditorEmails,
        });
        setAccounts(Array.isArray(detail.accounts) ? detail.accounts : []);
        setForm({
          name: detail.name || "",
          ownerEmail: detail.ownerEmail || "",
          defaultAssigneeEmail: detail.defaultAssigneeEmail || "",
          dueDate,
          auditorEmails,
        });
        setStatus("ready");
      })
      .catch((err) => {
        if (!cancelled) {
          setStatus("error");
          setError(err?.message || "Unable to load this assessment.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, auditId]);

  const userOptions = useMemo(() => {
    const options = excludeMspUsers(users, organization)
      .filter((user) => user.email)
      .map((user) => ({ email: user.email, label: formatUserLabel(user) }));
    const known = new Set(options.map((option) => option.email));
    if (form.ownerEmail && !known.has(form.ownerEmail) && !options.some((option) => option.email === form.ownerEmail)) {
      options.unshift({ email: form.ownerEmail, label: form.ownerEmail });
    }
    if (
      form.defaultAssigneeEmail &&
      !known.has(form.defaultAssigneeEmail) &&
      !options.some((option) => option.email === form.defaultAssigneeEmail)
    ) {
      options.unshift({ email: form.defaultAssigneeEmail, label: form.defaultAssigneeEmail });
    }
    return options;
  }, [users, organization, form.ownerEmail, form.defaultAssigneeEmail]);

  const auditorOptions = useMemo(() => {
    const options = auditorCandidates({ users, organization, msp: mspUsers })
      .filter((user) => user.email)
      .map((user) => ({ email: user.email, label: formatUserLabel(user) }));
    const known = new Set(options.map((option) => option.email));
    // A saved reviewer stays visible even if they are no longer in the selectable list.
    form.auditorEmails.forEach((email) => {
      if (email && !known.has(email)) options.unshift({ email, label: email });
    });
    return options;
  }, [users, organization, mspUsers, form.auditorEmails]);

  // Only a draft is fully editable; once launched, the due date is the one field that may move.
  const locked = Boolean(auditStatus) && auditStatus !== AUDIT_STATUSES.DRAFT;
  const name = form.name.trim();
  const nameChanged = name !== loaded.name;
  const ownerChanged = form.ownerEmail !== loaded.ownerEmail;
  const assigneeChanged = form.defaultAssigneeEmail !== loaded.defaultAssigneeEmail;
  const dueDateChanged = form.dueDate !== loaded.dueDate;
  const auditorsChanged = !sameEmails(form.auditorEmails, loaded.auditorEmails);
  const dirty =
    hasPendingAccounts ||
    (locked ? dueDateChanged : nameChanged || ownerChanged || assigneeChanged || dueDateChanged || auditorsChanged);
  const dueDateValid = hasDueDate(form.dueDate) && (!dueDateChanged || form.dueDate >= utcToday());
  const canSave =
    status === "ready" &&
    !saving &&
    dirty &&
    dueDateValid &&
    (locked ||
      (name.length > 0 &&
        name.length <= 200 &&
        hasEmail(form.ownerEmail) &&
        hasEmail(form.defaultAssigneeEmail) &&
        form.auditorEmails.length > 0 &&
        form.auditorEmails.every(hasEmail)));
  const ownerValue = userOptions.find((option) => option.email === form.ownerEmail) || null;
  const assigneeValue = userOptions.find((option) => option.email === form.defaultAssigneeEmail) || null;
  const auditorValues = auditorOptions.filter((option) => form.auditorEmails.includes(option.email));

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setNotice(null);
    try {
      if (scopeRef.current) await scopeRef.current.commitPending();
      const patchBody = {};
      if (locked) {
        if (dueDateChanged) patchBody.dueDate = form.dueDate;
      } else {
        if (nameChanged) patchBody.name = name;
        if (ownerChanged) patchBody.ownerEmail = form.ownerEmail;
        if (assigneeChanged) patchBody.defaultAssigneeEmail = form.defaultAssigneeEmail || null;
        if (dueDateChanged) patchBody.dueDate = form.dueDate;
      }
      if (Object.keys(patchBody).length) unwrapPost(await updateAudit(auditId, patchBody));
      if (!locked && auditorsChanged) {
        try {
          unwrapPost(await setAuditAuditors(auditId, form.auditorEmails));
        } catch (auditorError) {
          if (onRefresh) onRefresh();
          throw auditorError;
        }
      }
      if (auditStatus === AUDIT_STATUSES.DRAFT) {
        setConfirmLaunch(true);
        return;
      }
      onSaved();
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to update this assessment." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving || confirmLaunch ? undefined : onClose}
      disableEnforceFocus={confirmLaunch}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 800,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          py: 1.25,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
            Edit Assessment
          </Typography>
          {status === "ready" && auditStatus ? (
            <Typography role="note" sx={{ mt: 0.25, fontSize: "0.8125rem", lineHeight: 1.45, color: AH_WHITE }}>
              {auditCode ? (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {auditCode}
                </Box>
              ) : (
                "This assessment"
              )}{" "}
              is {AUDIT_STATUS_LABEL[auditStatus] || auditStatus}.{" "}
              {locked
                ? "Only the due date can be changed, and it cannot be set to a past date."
                : "Details, reviewers, and scope can be changed. New accounts are added when you save."}
            </Typography>
          ) : null}
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ p: "1.25rem 1.5rem", backgroundColor: AH_WHITE }}>
        {status === "loading" ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: AH_ACCENT }} />
          </Box>
        ) : status === "error" ? (
          <Typography sx={{ fontSize: "0.875rem", color: "#dc2626", py: 2 }}>{error}</Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: "1.5rem",
              rowGap: "1rem",
            }}
          >
            <Box>
              <FieldLabel required htmlFor="edit-audit-name">
                Assessment Name
              </FieldLabel>
              <TextField
                id="edit-audit-name"
                required
                fullWidth
                size="small"
                disabled={locked}
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Enter Assessment Name"
                inputProps={{ maxLength: 200 }}
                sx={fieldSx}
              />
            </Box>
            <Box>
              <FieldLabel required htmlFor="edit-audit-owner">
                Assessment Owner
              </FieldLabel>
              <Autocomplete
                size="small"
                disabled={locked}
                options={userOptions}
                loading={usersLoading}
                value={ownerValue}
                onChange={(_event, option) => setForm((prev) => ({ ...prev, ownerEmail: option?.email || "" }))}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(a, b) => a.email === b.email}
                slotProps={{ paper: { sx: optionPaperSx } }}
                renderInput={(params) => (
                  <TextField {...params} id="edit-audit-owner" required placeholder="Select Owner" sx={fieldSx} />
                )}
              />
            </Box>
            <Box>
              <FieldLabel required htmlFor="edit-audit-assignee">
                Default Assignee
              </FieldLabel>
              <Autocomplete
                size="small"
                disabled={locked}
                options={userOptions}
                loading={usersLoading}
                value={assigneeValue}
                onChange={(_event, option) =>
                  setForm((prev) => ({ ...prev, defaultAssigneeEmail: option?.email || "" }))
                }
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(a, b) => a.email === b.email}
                slotProps={{ paper: { sx: optionPaperSx } }}
                renderInput={(params) => (
                  <TextField {...params} id="edit-audit-assignee" required placeholder="Select Assignee" sx={fieldSx} />
                )}
              />
            </Box>
            <Box>
              <FieldLabel required htmlFor="edit-audit-due">
                Due Date
              </FieldLabel>
              <DueDateField
                id="edit-audit-due"
                required
                error={status === "ready" && !hasDueDate(form.dueDate)}
                helperText={status === "ready" && !hasDueDate(form.dueDate) ? "Due date is required." : ""}
                value={form.dueDate}
                onChange={(dueDate) => setForm((prev) => ({ ...prev, dueDate }))}
                fullWidth
                showLabel={false}
                size="small"
              />
            </Box>
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <FieldLabel required htmlFor="edit-audit-reviewers">
                Auditors / Reviewers
              </FieldLabel>
              <Autocomplete
                multiple
                size="small"
                disabled={locked}
                options={auditorOptions}
                loading={usersLoading || mspUsers.loading}
                loadingText="Loading MSP users…"
                noOptionsText={mspUsers.error || "No MSP users are available."}
                value={auditorValues}
                onChange={(_event, options) =>
                  setForm((prev) => ({ ...prev, auditorEmails: options.map((option) => option.email) }))
                }
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(a, b) => a.email === b.email}
                filterSelectedOptions
                slotProps={{ paper: { sx: optionPaperSx } }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} {...tagProps} label={option.label} size="small" sx={auditorChipSx} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="edit-audit-reviewers"
                    placeholder={auditorValues.length ? "" : "Select Auditors"}
                    sx={fieldSx}
                  />
                )}
              />
            </Box>
            <AuditScopeSection
              ref={scopeRef}
              auditId={auditId}
              auditStatus={auditStatus}
              initialAccounts={accounts}
              disabled={saving}
              onChanged={onRefresh}
              onNotice={setNotice}
              onPendingChange={onPendingChange}
            />
          </Box>
        )}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button onClick={onClose} disabled={saving || confirmLaunch} sx={{ ...dialogButtonSx, backgroundColor: CLOSE_BG, color: CLOSE_TEXT, "&:hover": { backgroundColor: "#636678", boxShadow: "none" } }}>
          Cancel
        </Button>
        {notice?.message ? (
          <Typography
            role={notice.severity === "success" ? "status" : "alert"}
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: NOTICE_COLOR[notice.severity] || NOTICE_COLOR.error,
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {notice.message}
          </Typography>
        ) : null}
        <Button
          onClick={save}
          disabled={!canSave}
          sx={{
            ...dialogButtonSx,
            backgroundColor: AH_ACCENT,
            color: AH_WHITE,
            "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Saving" : "Save"}
        </Button>
      </Box>
      <LaunchAuditDialog
        open={confirmLaunch}
        audit={{ auditId, auditCode, name, afterSave: true }}
        onClose={() => {
          setConfirmLaunch(false);
          onSaved();
        }}
        onLaunched={(audit, failures) => {
          setConfirmLaunch(false);
          if (onLaunched) onLaunched(audit, failures);
          else onSaved();
        }}
      />
    </Dialog>
  );
};

export default EditAuditDialog;
